// Tracks per-video playback position in localStorage so the CRT resumes
// where the user left off after a reload / channel hop / browser restart.
//
// Stored shape (single key for tiny size, easy to nuke):
//   localStorage.crt_watch_progress = {
//     "<videoId>": { t: number_seconds, d: duration_seconds, at: epoch_ms }
//   }

const STORAGE_KEY = "crt_watch_progress";
const MAX_ENTRIES = 250;          // LRU-style cap, ~25 KB max
const MIN_RESUME_SECONDS = 5;     // Anything earlier just restarts from 0
const TAIL_THRESHOLD_SECONDS = 15; // If within this many seconds of the end, treat as finished
const COMPLETION_RATIO = 0.95;    // ≥95% watched = treat as finished

function safeRead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

function safeWrite(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (e) { /* localStorage full or unavailable — silent */ }
}

export function getResumePosition(videoId, duration) {
  if (!videoId) return 0;
  const entry = safeRead()[videoId];
  if (!entry || typeof entry.t !== "number") return 0;
  if (entry.t < MIN_RESUME_SECONDS) return 0;
  if (Number.isFinite(duration) && duration > 0) {
    if (entry.t >= duration - TAIL_THRESHOLD_SECONDS) return 0;
    if (entry.t / duration >= COMPLETION_RATIO) return 0;
  }
  return entry.t;
}

export function saveResumePosition(videoId, currentTime, duration) {
  if (!videoId) return;
  if (!Number.isFinite(currentTime) || currentTime < MIN_RESUME_SECONDS) return;
  const map = safeRead();
  map[videoId] = {
    t: Math.floor(currentTime),
    d: Number.isFinite(duration) ? Math.floor(duration) : null,
    at: Date.now(),
  };
  // Evict oldest entries once we exceed the cap.
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    keys.sort((a, b) => (map[a].at || 0) - (map[b].at || 0));
    for (let i = 0; i < keys.length - MAX_ENTRIES; i++) {
      delete map[keys[i]];
    }
  }
  safeWrite(map);
}

export function clearResumePosition(videoId) {
  if (!videoId) return;
  const map = safeRead();
  if (videoId in map) {
    delete map[videoId];
    safeWrite(map);
  }
}

/**
 * Returns every video that has saved progress, with its raw `{t, d, at}`.
 * Used by the "Continue Watching" pseudo-channel.
 *
 * @param {Object} [opts]
 * @param {number} [opts.limit=12] - max entries to return
 * @returns {Array<{ videoId: string, t: number, d: number|null, at: number }>}
 *          sorted by `at` desc (most recently watched first)
 */
export function listResumeEntries({ limit = 12 } = {}) {
  const map = safeRead();
  const entries = Object.entries(map)
    .map(([videoId, e]) => ({ videoId, t: e.t, d: e.d, at: e.at || 0 }))
    .filter((e) => {
      // Same gating as getResumePosition: ignore too-early / finished entries.
      if (!Number.isFinite(e.t) || e.t < MIN_RESUME_SECONDS) return false;
      if (Number.isFinite(e.d) && e.d > 0) {
        if (e.t >= e.d - TAIL_THRESHOLD_SECONDS) return false;
        if (e.t / e.d >= COMPLETION_RATIO) return false;
      }
      return true;
    })
    .sort((a, b) => b.at - a.at);
  return limit ? entries.slice(0, limit) : entries;
}
